import UniversalCookie from 'universal-cookie';
import client from './apiClient';
import {
  FeeConfigurationViewModel,
  FeeHudLineViewModel,
  FeeRulesVersionViewModel,
  CreateOrUpdateFeeConfigRequestViewModel,
  CreateOrUpdateFeeHudLineViewModel,
  PublishFeeRulesVersionRequestViewModel,
  FeeRuleViewModel,
  QuestionResponseDefaultViewModel,
  CreateOrUpdateQuestionResponseDefaultViewModel,
  FeesResponseViewModel,
  FeeResponseViewModel,
} from '../models/fees';
import { SimpleBinaryExpression, SimpleExpression } from '../models/expression';
import { Constants } from '../utils/constants';

const cookie = new UniversalCookie({ path: '/' });

// ===== Sanitization Helpers =====

const stripRightValueFormat = (value: string | null | undefined): string => {
  if (!value) return '';
  return value
    .trim()
    .replace(/^["']/, '')
    .replace(/["']$/, '')
    .trim();
};

const sanitizeBinaryExpression = (expr: SimpleBinaryExpression): SimpleBinaryExpression => {
  return {
    ...expr,
    right: expr.right?.map(val => stripRightValueFormat(val)) || null,
  };
};

const sanitizeExpression = (expr: SimpleExpression | null | undefined): SimpleExpression | null => {
  if (!expr) return null;
  
  return {
    ...expr,
    expressions: expr.expressions?.map((item) => ({
      ...item,
      expression: item.expression ? sanitizeBinaryExpression(item.expression) : null,
      expressionGroup: item.expressionGroup ? sanitizeExpression(item.expressionGroup) : null,
    })) || null,
  };
};

const sanitizeRule = (rule: FeeRuleViewModel): FeeRuleViewModel => {
  return {
    ...rule,
    expression: sanitizeExpression(rule.expression),
  };
};

const sanitizeFeeConfiguration = (config: FeeConfigurationViewModel): FeeConfigurationViewModel => {
  return {
    ...config,
    rules: config.rules?.map(sanitizeRule) || null,
  };
};

const sanitizeFeeVersion = (version: FeeRulesVersionViewModel): FeeRulesVersionViewModel => {
  return {
    ...version,
    feeConfig: version.feeConfig?.map(sanitizeFeeConfiguration) || null,
  };
};

client.interceptors.request.use(async (config) => {
  const tenantId: string | null | undefined = localStorage.getItem(Constants.authorization.tenantId) || null;
  const access_token: string | undefined = cookie.get(Constants.authorization.accessToken) || null;

  config.headers['X-TenantId'] = tenantId;
  config.headers['Authorization'] = `Bearer ${access_token}`;

  return config;
});

// ===== Fee Configurations =====

export const getAllFeeConfigurations = async (): Promise<FeeConfigurationViewModel[]> => {
  const { data } = await client.get<FeeConfigurationViewModel[]>('/fees/rules/fee-configurations');
  return data.map(sanitizeFeeConfiguration);
};

export const getFeeConfiguration = async (id: number): Promise<FeeConfigurationViewModel> => {
  const { data } = await client.get<FeeConfigurationViewModel>(`/fees/rules/fee-configurations/${id}`);
  return sanitizeFeeConfiguration(data);
};

export const createFeeConfiguration = async (payload: CreateOrUpdateFeeConfigRequestViewModel): Promise<FeeConfigurationViewModel> => {
  const { data } = await client.post<FeeConfigurationViewModel>('/fees/rules/fee-configurations', payload);
  return sanitizeFeeConfiguration(data);
};

export const updateFeeConfiguration = async (id: number, payload: CreateOrUpdateFeeConfigRequestViewModel): Promise<FeeConfigurationViewModel> => {
  const { data } = await client.put<FeeConfigurationViewModel>(`/fees/rules/fee-configurations/${id}`, payload);
  return sanitizeFeeConfiguration(data);
};

export const deleteFeeConfiguration = async (id: number): Promise<void> => {
  await client.delete(`/fees/rules/fee-configurations/${id}`);
};

export const importFeeConfigurations = async (file: File): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await client.post('/fees/rules/fee-configurations/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

// ===== HUD Lines =====

export const getAllHudLines = async (feeRulesVersionId?: number | null): Promise<FeeHudLineViewModel[]> => {
  const { data } = await client.get<FeeHudLineViewModel[]>('/fees/hud-lines', {
    params: feeRulesVersionId !== undefined && feeRulesVersionId !== null ? { feeRulesVersionId } : undefined,
  });
  return data;
};

export const getHudLine = async (id: number): Promise<FeeHudLineViewModel> => {
  const { data } = await client.get<FeeHudLineViewModel>(`/fees/hud-lines/${id}`);
  return data;
};

export const createHudLine = async (payload: CreateOrUpdateFeeHudLineViewModel): Promise<FeeHudLineViewModel> => {
  const { data } = await client.post<FeeHudLineViewModel>('/fees/hud-lines', payload);
  return data;
};

export const updateHudLine = async (id: number, payload: CreateOrUpdateFeeHudLineViewModel): Promise<FeeHudLineViewModel> => {
  const { data } = await client.put<FeeHudLineViewModel>(`/fees/hud-lines/${id}`, payload);
  return data;
};

export const deleteHudLine = async (id: number): Promise<void> => {
  await client.delete(`/fees/hud-lines/${id}`);
};

export const importHudLines = async (file: File): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await client.post('/fees/hud-lines/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

// ===== Versions =====

export const getAllVersions = async (): Promise<FeeRulesVersionViewModel[]> => {
  const { data } = await client.get<FeeRulesVersionViewModel[]>('/fees/rules/versions');
  return data.map(sanitizeFeeVersion);
};

export const getVersion = async (id: number): Promise<FeeRulesVersionViewModel> => {
  const { data } = await client.get<FeeRulesVersionViewModel>(`/fees/rules/versions/${id}`);
  return sanitizeFeeVersion(data);
};

export const publishVersion = async (payload: PublishFeeRulesVersionRequestViewModel): Promise<FeeRulesVersionViewModel> => {
  const { data } = await client.post<FeeRulesVersionViewModel>('/fees/rules/versions/publish', payload);
  return sanitizeFeeVersion(data);
};

// ===== Fee Calculation =====

export const calculateSingleProductFees = async (payload: any): Promise<FeesResponseViewModel> => {
  const { data } = await client.post<FeesResponseViewModel>('/fees/single', payload);
  return data;
};

export const calculateMultipleProductFees = async (payload: any): Promise<FeeResponseViewModel[]> => {
  const { data } = await client.post<FeeResponseViewModel[]>('/fees', payload);
  return data;
};

// ===== Question Response Defaults =====

export const getAllQuestionResponseDefaults = async (): Promise<QuestionResponseDefaultViewModel[]> => {
  const { data } = await client.get<QuestionResponseDefaultViewModel[]>('/fees/question-response-defaults');
  return data;
};

export const getQuestionResponseDefault = async (id: number): Promise<QuestionResponseDefaultViewModel> => {
  const { data } = await client.get<QuestionResponseDefaultViewModel>(`/fees/question-response-defaults/${id}`);
  return data;
};

export const createQuestionResponseDefault = async (
  payload: CreateOrUpdateQuestionResponseDefaultViewModel
): Promise<QuestionResponseDefaultViewModel> => {
  const { data } = await client.post<QuestionResponseDefaultViewModel>('/fees/question-response-defaults', payload);
  return data;
};

export const updateQuestionResponseDefault = async (
  id: number,
  payload: CreateOrUpdateQuestionResponseDefaultViewModel
): Promise<QuestionResponseDefaultViewModel> => {
  const { data } = await client.put<QuestionResponseDefaultViewModel>(
    `/fees/question-response-defaults/${id}`,
    payload
  );
  return data;
};

export const deleteQuestionResponseDefault = async (id: number): Promise<void> => {
  await client.delete(`/fees/question-response-defaults/${id}`);
};

